import React from 'react';
import { useTranslation } from 'react-i18next';
import { RESULT_ACCENTS, type ResultCategoryKey } from './accents';
import { MeterRow, ResultCard } from './primitives';
import type { PreviousAttemptInfo } from './attemptDelta';
import { DTM_POINTS_PER_QUESTION } from './categoryResult';

export interface PlatformAverageInfo {
  percentage: number;
  attempts: number;
}

interface AttemptHistoryProps {
  category: ResultCategoryKey;
  /** This attempt's figure in the unit of its category. */
  current: { percentage: number; band: number | null; total: number | null };
  /** The learner's earlier attempts, oldest first. */
  history: PreviousAttemptInfo[];
  platform?: PlatformAverageInfo | null;
  /** DTM: the subject's items, so the share of an earlier attempt becomes points. */
  dtm?: { items: number; max: number } | null;
  className?: string;
}

interface Point {
  label: string;
  value: string;
  bar: number;
  current?: boolean;
}

const fmt1 = (n: number) => n.toFixed(1);

/** Your attempts of this test side by side, and the platform average when enough people took it. */
export function AttemptHistory({ category, current, history, platform, dtm, className }: AttemptHistoryProps) {
  const { t } = useTranslation();
  const accent = RESULT_ACCENTS[category];
  if (history.length === 0 && !platform) return null;

  const pointsOf = (percentage: number) => (dtm ? Math.round((percentage / 100) * dtm.items) * DTM_POINTS_PER_QUESTION : null);
  const figure = (entry: { percentage: number; band: number | null; total: number | null }): { text: string; bar: number; number: number } => {
    if (category === 'ielts' && entry.band !== null) return { text: fmt1(entry.band), bar: (entry.band / 9) * 100, number: entry.band };
    if (category === 'sat' && entry.total !== null) return { text: String(entry.total), bar: (entry.total / 1600) * 100, number: entry.total };
    const points = pointsOf(entry.percentage);
    if (points !== null && dtm) return { text: t('result.view.points', { points: fmt1(points) }), bar: dtm.max ? (points / dtm.max) * 100 : 0, number: points };
    return { text: `${entry.percentage}%`, bar: entry.percentage, number: entry.percentage };
  };

  const rows: Point[] = [
    ...history.map((h, i) => ({ label: t('result.view.attempt_n', { n: i + 1 }), ...(({ text, bar }) => ({ value: text, bar }))(figure(h)) })),
    { label: t('result.view.attempt_this', { n: history.length + 1 }), ...(({ text, bar }) => ({ value: text, bar }))(figure(current)), current: true },
  ];
  const first = history.length > 0 ? figure(history[0]) : null;
  const now = figure(current);
  const diff = first ? now.number - first.number : 0;
  const unit = category === 'ielts' ? 'band' : category === 'sat' ? 'score' : dtm ? 'points' : 'percent';
  const diffText = unit === 'band' ? `${diff > 0 ? '+' : diff < 0 ? '−' : ''}${fmt1(Math.abs(diff))}` : `${diff > 0 ? '+' : diff < 0 ? '−' : ''}${unit === 'points' ? fmt1(Math.abs(diff)) : Math.round(Math.abs(diff))}${unit === 'percent' ? '%' : ''}`;

  return (
    <ResultCard accent={accent} title={t(category === 'ielts' ? 'result.view.history_band' : 'result.view.history_title')} className={className}>
      <div className="mt-2">
        {rows.map((row) => (
          <MeterRow key={row.label} label={row.label} value={row.value} percent={row.bar} color={row.current ? accent.fg : '#9AA3B8'} />
        ))}
        {platform && (
          <MeterRow
            label={t('result.view.platform_average')}
            value={dtm ? t('result.view.points', { points: fmt1((platform.percentage / 100) * dtm.max) }) : `${platform.percentage}%`}
            percent={platform.percentage}
            color="#4B5468"
            note={t('result.view.platform_note', { count: platform.attempts })}
          />
        )}
      </div>
      {first && diff !== 0 && <p className="mt-4 text-[12.5px] text-[#4B5468]">{t('result.view.vs_first', { delta: diffText })}</p>}
    </ResultCard>
  );
}
